/**
 * @fileoverview ProcessingOverlay Component - Loading State Display
 *
 * This component displays a loading overlay during AI processing operations.
 * Shows a spinner animation and customizable status message.
 * Can also show completion state with OK button and stats.
 *
 * @description
 * Key features:
 * - Centered loading spinner with animation
 * - Dynamic status message display
 * - Semi-transparent overlay design
 * - Completion state with success message, stats, and OK button
 *
 * @requires React
 */
import React from 'react';
import DonateSection from './DonateSection';

/**
 * ProcessingOverlay Component
 *
 * @param {Object} props
 * @param {string} props.status - The current processing status message
 * @param {boolean} props.isComplete - Whether processing is complete
 * @param {string} props.completeTitle - Title to show on completion
 * @param {Object} props.stats - Stats to show on completion { detected, blurred, files }
 * @param {Function} props.onComplete - Callback when OK button is clicked
 * @returns {JSX.Element}
 */
function ProcessingOverlay({
  status,
  isComplete = false,
  completeTitle = 'Complete!',
  stats = null,
  onComplete
}) {
  /* ========================================================================
     Render
     ======================================================================== */

  // Show completion state
  if (isComplete) {
    return (
      <div className="max-w-xl mx-auto text-center">
        {/* Completion Box */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-8 sm:p-12 mb-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-4 text-emerald-400">{completeTitle}</h3>

          {/* Stats boxes - similar to PreviewStep: Detected, Blurred, Files */}
          {stats && (
            <div className="flex justify-center gap-3 sm:gap-4 mb-4">
              <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-[70px]">
                <div className="text-lg sm:text-xl font-bold text-emerald-400">{stats.detected}</div>
                <div className="text-gray-400 text-xs sm:text-sm">Detected</div>
              </div>
              <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-[70px]">
                <div className="text-lg sm:text-xl font-bold text-green-400">{stats.blurred}</div>
                <div className="text-gray-400 text-xs sm:text-sm">Blurred</div>
              </div>
              <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-[70px]">
                <div className="text-lg sm:text-xl font-bold text-blue-400">{stats.files}</div>
                <div className="text-gray-400 text-xs sm:text-sm">Files</div>
              </div>
            </div>
          )}

          {/* Only show download message if files were actually downloaded */}
          {stats && stats.blurred > 0 && (
            <p className="text-gray-400 mb-6">Check your Downloads folder.</p>
          )}
          {(!stats || stats.blurred === 0) && (
            <p className="text-gray-400 mb-6">No faces were detected to blur.</p>
          )}

          <button
            onClick={onComplete}
            className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-xl font-medium transition-colors"
          >
            OK
          </button>
        </div>

        {/* Support/Donate Section - Outside completion box */}
        <DonateSection className="mb-0" />
      </div>
    );
  }

  // Show processing state
  return (
    <div className="max-w-xl mx-auto text-center">
      <div className="bg-white/5 backdrop-blur rounded-2xl p-8 sm:p-12">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <svg className="w-10 h-10 text-emerald-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold mb-2">Processing...</h3>
        <p className="text-gray-400">{status}</p>
      </div>
    </div>
  );
}

export default ProcessingOverlay;
